import * as React from 'react'

import { Box, Button, Skeleton } from '@factorialco/deprecated-design-system'
import type { Field } from 'factorial-form'
import {
  AllowanceDataFragment,
  BulkAssignEmployeesTimeoffPolicyMappingsInput,
  DestroyPolicyDocument,
  ModalFetchPolicyAllowancesDocument,
  TimeoffPoliciesRemoveModalPageQuery,
  TimeoffPolicyAddEmployeeBulkAssignEmployeesDocument,
  TimeoffPolicyEmployeesCollectionListDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Select from 'modules/core/components/Inputs/Select'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import AllowanceMapper from 'modules/timeoff/components/TimeoffAllowances/AllowanceMapper'
import PolicyForm from 'modules/timeoff/lib/TimeoffPolicy/Select'

interface Props {
  loading: boolean
  data: TimeoffPoliciesRemoveModalPageQuery | undefined
  onClose: () => void
}

interface FormState {
  step: 'select_policy' | 'adjustment_loss'
  timeoffPolicyAllowances: AllowanceDataFragment[]
  mappings: BulkAssignEmployeesTimeoffPolicyMappingsInput[]
}

const Content = ({
  data,
  formState,
  setFormState,
  policyForm,
}: {
  data: TimeoffPoliciesRemoveModalPageQuery | undefined
  formState: FormState
  setFormState: (formState: FormState) => void
  policyForm: PolicyForm
}) => {
  const [fetchPolicyAllowances] = useLazyQuery(ModalFetchPolicyAllowancesDocument)

  const policyToRemove = data?.timeoff.policyToRemove

  if (!policyToRemove) {
    throw new Error('Policy to remove not found')
  }

  // NOTE: ideally we should get this employee data from the policyToRemove node but the
  //       employeeNames relationship is missing.
  const employeesData = data?.employees.employeeNames ?? []

  const policiesData =
    data?.timeoff.policies.filter((policy) => policy.id !== policyToRemove.id) ?? []

  const options = policiesData.map(({ name, id }) => ({
    label: name,
    value: id,
  }))

  const onSelect = async (field: Field) => {
    const { data: allowanceData } = await fetchPolicyAllowances({
      variables: {
        timeoffPolicyId: String(field.value),
      },
    })

    setFormState({
      ...formState,
      timeoffPolicyAllowances: allowanceData?.timeoff.policy?.allowances ?? [],
    })
  }

  return (
    <>
      {formState.step === 'select_policy' && employeesData.length > 0 && (
        <>
          <FormRow>
            <Select
              none={i18n.t('common.none')}
              field={policyForm.get('timeoff_policy_id')}
              label={i18n.t('settings.timeoff_policies.fields.name')}
              options={options}
              onChange={(field) => onSelect(field)}
            />
          </FormRow>
          {policyToRemove.allowanceIncidences > 0 &&
            policyToRemove.allowances.length > 0 &&
            formState.timeoffPolicyAllowances.length > 0 && (
              <FormRow>
                <AllowanceMapper
                  policyId={policyToRemove.id}
                  allowancesToMap={policyToRemove.allowances}
                  timeoffPolicyAllowances={formState.timeoffPolicyAllowances}
                  onMappingsChange={(mappings) => setFormState({ ...formState, mappings })}
                />
              </FormRow>
            )}
        </>
      )}
    </>
  )
}

const Footer = ({
  loading,
  data,
  formState,
  setFormState,
  policyForm,
  onClose,
}: {
  loading: boolean
  data: TimeoffPoliciesRemoveModalPageQuery | undefined
  formState: FormState
  setFormState: (formState: FormState) => void
  policyForm: PolicyForm
  onClose: () => void
}) => {
  const [bulkAssignEmployees] = useMutation(TimeoffPolicyAddEmployeeBulkAssignEmployeesDocument, {
    extractorFn: (mutationData) => mutationData.timeoffMutations.bulkAssignEmployeesTimeoffPolicy,
  })
  const [destroyPolicy] = useMutation(DestroyPolicyDocument, {
    extractorFn: (mutationData) => mutationData.timeoffMutations.deleteTimeoffPolicy,
  })

  if (loading) {
    return <Skeleton height="s24" />
  }

  const policyToRemove = data?.timeoff.policyToRemove

  if (!policyToRemove) {
    throw new Error('Policy to remove not found')
  }

  // NOTE: ideally we should get this employee data from the policyToRemove node but the
  //       employeeNames relationship is missing.
  const employeesData = data?.employees.employeeNames ?? []

  const onSubmit = async () => {
    const selectedPolicyId = String(policyForm.get('timeoff_policy_id').value)

    if (employeesData.length > 0) {
      await bulkAssignEmployees({
        variables: {
          employeeIds: employeesData.map((employee) => employee.id),
          mappings: formState.mappings,
          policyId: selectedPolicyId,
        },
        refetchQueries: [TimeoffPolicyEmployeesCollectionListDocument],
      })
    }

    await destroyPolicy({
      variables: {
        timeoffPolicyId: policyToRemove.id,
      },
      update(cache) {
        cache.evict({ id: cache.identify(policyToRemove) })
      },
    })

    onClose()
  }

  return (
    <>
      {formState.step === 'select_policy' && (
        <Button
          onClick={async () => {
            if (
              employeesData.length === 0 ||
              policyToRemove.allowanceIncidences === 0 ||
              (formState.mappings.length > 0 &&
                formState.mappings.every((mapping) => mapping.newAllowanceId !== '0'))
            ) {
              return onSubmit()
            } else {
              setFormState({
                ...formState,
                step: 'adjustment_loss',
              })
            }
          }}
        >
          {i18n.t('common.submit')}
        </Button>
      )}
      {formState.step === 'adjustment_loss' && (
        <Box flexDirection="row" alignItems="center" justifyContent="spaceEvenly" gap="s8">
          <Button
            type="positive"
            onClick={() => {
              setFormState({
                ...formState,
                step: 'select_policy',
              })
            }}
          >
            {i18n.t('settings.timeoff_policies.adjustment_loss_modal.primary_button')}
          </Button>
          <Button
            type="negative"
            onClick={() => {
              return onSubmit()
            }}
          >
            {i18n.t('settings.timeoff_policies.adjustment_loss_modal.secondary_button')}
          </Button>
        </Box>
      )}
    </>
  )
}

const RemovePolicy = ({ loading, data, onClose }: Props) => {
  const [formState, setFormState] = React.useState<FormState>({
    step: 'select_policy',
    timeoffPolicyAllowances: [],
    mappings: [],
  })
  const policyForm = React.useMemo(() => new PolicyForm({}), [])

  const title =
    formState.step === 'adjustment_loss'
      ? i18n.t('settings.timeoff_policies.adjustment_loss_modal.title')
      : i18n.t('settings.timeoff_policies.remove_modal.title')

  // TODO: the description does not make any sense when there are no employees
  const description =
    formState.step === 'adjustment_loss'
      ? i18n.t('settings.timeoff_policies.adjustment_loss_modal.description')
      : i18n.t('settings.timeoff_policies.move_employee_modal.description_complete')

  return (
    <Modal
      illustration="holidays-event-cancel"
      title={title}
      description={description}
      onClose={onClose}
      footer={
        <Footer
          loading={loading}
          data={data}
          formState={formState}
          setFormState={setFormState}
          policyForm={policyForm}
          onClose={onClose}
        />
      }
    >
      {loading ? (
        <Loading />
      ) : (
        <Content
          data={data}
          formState={formState}
          setFormState={setFormState}
          policyForm={policyForm}
        />
      )}
    </Modal>
  )
}

export default observer(RemovePolicy)
